import React, { useState, useEffect } from 'react';
import { Users, Circle, Monitor, X, UserCheck, UserX } from 'lucide-react';
import { useOnlineUsers } from '../hooks/useOnlineUsers';
import axios from 'axios';
import './OnlineUsersCard.css';

const OnlineUsersCard = () => {
  const { onlineCount, onlineUsers, loading: onlineLoading } = useOnlineUsers();
  const [showModal, setShowModal] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [offlineUsers, setOfflineUsers] = useState([]);
  const [loadingAll, setLoadingAll] = useState(false);

  const getRoleBadge = (role) => {
    const badges = {
      super_admin: { label: 'Super Admin', color: '#dc2626' },
      admin: { label: 'Admin', color: '#ea580c' },
      user: { label: 'Usuário', color: '#2563eb' },
      observer: { label: 'Observador', color: '#64748b' }
    };
    return badges[role] || badges.user;
  };

  const formatConnectionTime = (connectedAt) => {
    const now = new Date();
    const connected = new Date(connectedAt);
    const diffMs = now - connected;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Agora mesmo';
    if (diffMins === 1) return '1 minuto atrás';
    if (diffMins < 60) return `${diffMins} minutos atrás`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours === 1) return '1 hora atrás';
    return `${diffHours} horas atrás`;
  };

  // Buscar todos os usuários
  useEffect(() => {
    const fetchAllUsers = async () => {
      setLoadingAll(true);
      try {
        const response = await axios.get('/api/users', { withCredentials: true });
        if (response.data.success) {
          setAllUsers(response.data.data);
        }
      } catch (error) {
        console.error('Erro ao buscar usuários:', error);
      } finally {
        setLoadingAll(false);
      }
    };

    fetchAllUsers();
  }, []);

  // Calcular usuários offline
  useEffect(() => {
    if (allUsers.length > 0 && onlineUsers.length >= 0) {
      const onlineIds = new Set(onlineUsers.map(u => u.id));
      const offline = allUsers.filter(u => !onlineIds.has(u.id));
      setOfflineUsers(offline);
    }
  }, [allUsers, onlineUsers]);

  const openModal = (type) => {
    setShowModal(type);
  };

  const closeModal = () => {
    setShowModal(null);
  };

  const getModalData = () => {
    switch (showModal) {
      case 'online':
        return { title: 'Usuários Online', users: onlineUsers, color: '#10b981', icon: UserCheck };
      case 'offline':
        return { title: 'Usuários Offline', users: offlineUsers, color: '#64748b', icon: UserX };
      case 'total':
        return { title: 'Todos os Usuários', users: allUsers, color: '#3b82f6', icon: Users };
      default:
        return { title: '', users: [], color: '#3b82f6', icon: Users };
    }
  };

  const modalData = getModalData();
  const ModalIcon = modalData.icon;

  return (
    <>
      {/* Grid de 3 Cards */}
      <div className="users-cards-grid">
        {/* Card 1: Usuários Online */}
        <div className="user-stat-card online-card">
          <div className="card-icon-wrapper" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}>
            <UserCheck size={24} />
          </div>
          <div className="card-content">
            <span className="card-label">Usuários Online</span>
            <span className="card-value">{onlineLoading ? '...' : onlineCount}</span>
          </div>
          <button 
            className="card-list-button"
            onClick={() => openModal('online')}
            disabled={onlineCount === 0}
          >
            Listar
          </button>
        </div>

        {/* Card 2: Usuários Offline */}
        <div className="user-stat-card offline-card">
          <div className="card-icon-wrapper" style={{ background: 'linear-gradient(135deg, #64748b 0%, #475569 100%)' }}>
            <UserX size={24} />
          </div>
          <div className="card-content">
            <span className="card-label">Usuários Offline</span>
            <span className="card-value">{loadingAll ? '...' : offlineUsers.length}</span>
          </div>
          <button 
            className="card-list-button"
            onClick={() => openModal('offline')}
            disabled={offlineUsers.length === 0}
          >
            Listar
          </button>
        </div>

        {/* Card 3: Total de Usuários */}
        <div className="user-stat-card total-card">
          <div className="card-icon-wrapper" style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' }}>
            <Users size={24} />
          </div>
          <div className="card-content">
            <span className="card-label">Total de Usuários</span>
            <span className="card-value">{loadingAll ? '...' : allUsers.length}</span>
          </div>
          <button 
            className="card-list-button"
            onClick={() => openModal('total')}
            disabled={allUsers.length === 0}
          >
            Listar
          </button>
        </div>
      </div>

      {/* Modal Unificado */}
      {showModal && (
        <div className="online-users-modal-overlay" onClick={closeModal}>
          <div className="online-users-modal" onClick={(e) => e.stopPropagation()}>
            {/* Header do Modal */}
            <div className="modal-header">
              <div className="modal-title">
                <ModalIcon size={24} style={{ color: modalData.color }} />
                <h2>{modalData.title}</h2>
                <div className="modal-badge" style={{ background: modalData.color }}>
                  {showModal === 'online' && <Circle className="pulse-icon" size={12} fill="currentColor" />}
                  <span>{modalData.users.length}</span>
                </div>
              </div>
              <button className="modal-close" onClick={closeModal}>
                <X size={20} />
              </button>
            </div>

            {/* Body do Modal */}
            <div className="modal-body">
              {(onlineLoading || loadingAll) ? (
                <div className="modal-loading">
                  <div className="spinner"></div>
                  <p>Carregando usuários...</p>
                </div>
              ) : modalData.users.length === 0 ? (
                <div className="modal-empty">
                  <ModalIcon size={64} opacity={0.2} />
                  <p>Nenhum usuário encontrado</p>
                </div>
              ) : (
                <div className="modal-users-list">
                  {modalData.users.map((user) => {
                    const roleBadge = getRoleBadge(user.role);
                    const isOnline = showModal === 'online' || onlineUsers.some(u => u.id === user.id);
                    const onlineUser = onlineUsers.find(u => u.id === user.id);
                    
                    return (
                      <div key={user.id} className="modal-user-item">
                        <div className="modal-user-avatar">
                          {showModal !== 'offline' && (
                            <Circle 
                              className="modal-status-dot" 
                              size={10} 
                              fill={isOnline ? "#10b981" : "#94a3b8"} 
                            />
                          )}
                          <div className="modal-avatar-circle">
                            {user.name.charAt(0).toUpperCase()}
                          </div>
                        </div>
                        <div className="modal-user-info">
                          <div className="modal-user-name">{user.name}</div>
                          <div className="modal-user-email">{user.email}</div>
                          <div className="modal-user-meta">
                            <span 
                              className="modal-role-badge" 
                              style={{ backgroundColor: roleBadge.color }}
                            >
                              {roleBadge.label}
                            </span>
                            {showModal === 'online' && onlineUser && (
                              <>
                                <span className="modal-connection-info">
                                  <Monitor size={12} />
                                  {onlineUser.activeConnections} {onlineUser.activeConnections === 1 ? 'conexão' : 'conexões'}
                                </span>
                                <span className="modal-time">{formatConnectionTime(onlineUser.connectedAt)}</span>
                              </>
                            )}
                            {showModal === 'total' && (
                              <span className={`modal-status-text ${isOnline ? 'online' : 'offline'}`}>
                                {isOnline ? 'Online' : 'Offline'}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OnlineUsersCard;
